/**
 * Mock data for Patients age detail (Operations).
 * Chart series comes from operationsMockData; replace table with API when ready.
 */

import type { RangeKey } from '@dataTypes/dashboard.types'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { getPatientsAgeData } from '@components/pages/Dashboard/Operations/operationsMockData'

export type PatientsAgeDetailRow = {
  id: string
  patientName: string
  age: number
  gender: 'Male' | 'Female'
  ageBracket: string
  lastVisit: string
}

const PATIENTS_FIGMA_1D: PatientsAgeDetailRow[] = [
  {
    id: '1',
    patientName: 'Sophia Martinez',
    age: 42,
    gender: 'Female',
    ageBracket: '18-24',
    lastVisit: 'June 21, 2026',
  },
  {
    id: '2',
    patientName: "Liam O'Connor",
    age: 29,
    gender: 'Male',
    ageBracket: '25-34',
    lastVisit: 'July 4, 2026',
  },
  {
    id: '3',
    patientName: 'Isabella Johnson',
    age: 68,
    gender: 'Female',
    ageBracket: '35-44',
    lastVisit: 'August 15, 2026',
  },
  {
    id: '4',
    patientName: 'Ethan Chen',
    age: 35,
    gender: 'Male',
    ageBracket: '45-54',
    lastVisit: 'September 30, 2026',
  },
  {
    id: '5',
    patientName: 'Maya Patel',
    age: 53,
    gender: 'Female',
    ageBracket: '55-64',
    lastVisit: 'October 12, 2026',
  },
  {
    id: '6',
    patientName: 'Noah Williams',
    age: 61,
    gender: 'Male',
    ageBracket: '65+',
    lastVisit: 'November 23, 2026',
  },
  {
    id: '7',
    patientName: 'Ava Robinson',
    age: 18,
    gender: 'Female',
    ageBracket: '0-17',
    lastVisit: 'December 7, 2026',
  },
  {
    id: '8',
    patientName: 'Lucas Nguyen',
    age: 47,
    gender: 'Male',
    ageBracket: '65+',
    lastVisit: 'January 18, 2026',
  },
  {
    id: '9',
    patientName: 'Emily Davis',
    age: 22,
    gender: 'Female',
    ageBracket: '0-17',
    lastVisit: 'February 28, 2026',
  },
  {
    id: '10',
    patientName: 'Benjamin Lee',
    age: 59,
    gender: 'Male',
    ageBracket: '18-24',
    lastVisit: 'March 10, 2026',
  },
]

const BRACKETS = ['0-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']

const EXTRA_NAMES = [
  'Jack F. Monroe',
  'Paula P. Tran',
  'James R. Huang',
  'Maria E. Lopez',
  'Ahmed J. Khan',
  'Yuki Tanaka',
  'Carlos M. Rivera',
  'Fatima A. Ali',
  'Lisa M. Chen',
  'David J. Smith',
]

function bracketForAge(age: number): string {
  if (age <= 17) return '0-17'
  if (age <= 24) return '18-24'
  if (age <= 34) return '25-34'
  if (age <= 44) return '35-44'
  if (age <= 54) return '45-54'
  if (age <= 64) return '55-64'
  return '65+'
}

function fmtCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n)
}

export function getPatientsAgeDetailSummary(range: RangeKey): {
  totalPatients: string
  male: string
  female: string
  largestBracket: string
} {
  const rows = getPatientsAgeData(range)
  let male = 0
  let female = 0
  let top = rows[0]
  let topSum = 0
  for (const r of rows) {
    male += r.Male
    female += r.Female
    const s = r.Male + r.Female
    if (s > topSum) {
      topSum = s
      top = r
    }
  }
  return {
    totalPatients: fmtCount(male + female),
    male: fmtCount(male),
    female: fmtCount(female),
    largestBracket: top?.label ?? '—',
  }
}

export function getPatientsAgeDetailMetrics(
  range: RangeKey,
): DashboardMetricItemData[] {
  const s = getPatientsAgeDetailSummary(range)
  return [
    {
      id: 'patients-total',
      leftLabel: 'Total patients',
      value: s.totalPatients,
    },
    { id: 'patients-male', leftLabel: 'Male', value: s.male },
    { id: 'patients-female', leftLabel: 'Female', value: s.female },
    {
      id: 'patients-largest-bracket',
      leftLabel: 'Largest bracket',
      value: s.largestBracket,
    },
  ]
}

export function getPatientsAgeDetailTableData(
  range: RangeKey,
): PatientsAgeDetailRow[] {
  if (range === '1D') return PATIENTS_FIGMA_1D

  const n = 10
  const visits = [
    'June 21, 2026',
    'July 4, 2026',
    'August 15, 2026',
    'September 30, 2026',
    'October 12, 2026',
    'November 23, 2026',
    'December 7, 2026',
    'January 18, 2026',
    'February 28, 2026',
    'March 10, 2026',
  ]

  return Array.from({ length: n }, (_, i) => {
    const base = PATIENTS_FIGMA_1D[i % PATIENTS_FIGMA_1D.length]
    const gender: 'Male' | 'Female' = i % 2 === 0 ? 'Male' : 'Female'
    const bracketKey = BRACKETS[i % BRACKETS.length]
    const age =
      bracketKey === '0-17'
        ? 8 + (i % 10)
        : bracketKey === '18-24'
          ? 19 + (i % 6)
          : bracketKey === '25-34'
            ? 26 + (i % 9)
            : bracketKey === '35-44'
              ? 36 + (i % 9)
              : bracketKey === '45-54'
                ? 46 + (i % 9)
                : bracketKey === '55-64'
                  ? 56 + (i % 9)
                  : 67 + (i % 15)
    return {
      id: `age-${range}-${i}`,
      patientName: EXTRA_NAMES[i % EXTRA_NAMES.length] ?? base.patientName,
      age,
      gender,
      ageBracket: bracketForAge(age),
      lastVisit: visits[i % visits.length],
    }
  })
}
